import { PassedSundaysWrittenDownMark } from '../../../../../common/src/api_types.ts';
import { DateString } from '../../../../../common/src/date_string.ts';
import { Org, OrgType, RepeatKind, SongList, SongListEntry, SongListSchema } from '../../../../../common/src/model.ts';
import {
  getChainedWrites,
  getPassedSundayRangeWrites,
  getPassedSundaysWrittenDown,
  getUnwrittenPassedSundays,
  isSundayWorshipService,
} from '../../../../../common/src/passed_sundays.ts';
import { writePassedSundaysDown } from '../../../data/passed_sundays_written_through.ts';
import { synchronizeAllSongLists } from '../../../data/use_current_org.tsx';
import { getColumnClearWrite, getPasteWrites, getRepeatingCellWrites } from './repeating_row_cells.ts';
import { AllSongLists } from './use_all_song_lists.ts';

// A church's planner edit made while a passed Sunday is not yet written down stands (issue #4209). Michael, on #4192:
// "Turning a repeat off, switching its kind, and Delete Row write down any passed Sunday not yet written before they
// change anything, so the outcome never depends on timing." Every edit of a church's weekly lists does the same: typing
// or choosing a song, editing verses, Clear (Delete, Backspace, the cell menu, an editor closed empty), paste and the
// column clear. While a passed Sunday is not yet written down (the planner fills it on screen: from the fill start up
// to the Sunday before the current one), the edit first writes those Sundays down as the server would
// (getPassedSundayRangeWrites: each repeating row's fill written into the Sunday, its blanks moved on to the next
// Sunday in scope), and then applies itself, both in one write, which tells the server the planner has written the
// church down through the last of them (writePassedSundaysDown) so its own write-down leaves them alone. Every edit in
// that window writes down, not only one whose lists touch the passed Sunday: writing down only records what the Sundays
// showed, and an edit of the current Sunday must not meet a passed blank the server has yet to move (Delete on the
// Sunday after a passed blank, which the carry would otherwise put back on every press).
//
// The edit is decided on what the admin saw, the lists as the planner drew them, and applied to the lists as written
// down, so the passed Sunday keeps every other row's written fill and a blank stands where it was moved. An edit that
// writes nothing (an unchanged entry, Clear with nothing to clear) writes nothing down. Schools and small groups write
// their edits exactly as before, with nothing written down: they have no repeating rows and no passed-Sunday rule, so
// their paste and column clear make the calls they made before (applyPaste, applyColumnClear), and the planner leaves
// their cells writing their own week's list.

// An edit of the org's lists: the lists to write, decided on `shown` (the lists as the planner drew them) and built on
// `written` (the same lists with the passed Sundays not yet written down written down; `shown` itself when there are
// none). None when the edit changes nothing.
export type PlannerEdit = (lists: { shown: SongList[]; written: SongList[] }) => SongList[];

export interface PlannerEditContext {
  org: Org | undefined;
  songLists: SongList[]; // the org's lists as the planner drew them
  currentWeekDate: DateString; // the current Sunday
  // the first Sunday the fill applies to on screen (getFillStartSunday), as the planner drew the lists: the passed
  // Sundays from it up to the one before the current Sunday are the ones not yet written down
  // (getUnwrittenPassedSundays), so an edit is judged on exactly what was drawn
  fillStartDate: DateString;
}

export interface PlannerEditWrites {
  lists: SongList[]; // the lists to write in one updateEach, a list with no entry being one to delete
  // the first and last passed Sundays the write writes down, which writePassedSundaysDown records; undefined when it
  // writes none
  writtenDown?: PassedSundaysWrittenDownMark;
}

/**
 * The writes of `edit` (see the module comment): for a church with a passed Sunday not yet written down, the write-down
 * of those Sundays and then the edit, built on the lists as written down and chained with it (getChainedWrites), so a
 * list both touch is written once with its final content and a list the write-down removed and the edit writes again
 * keeps its id; with `writtenDown` the first and last of those Sundays. Nothing at all, not even the write-down, when
 * the edit writes nothing. For any other org, and for a church with every passed Sunday written down, the edit's lists
 * as it builds them on the lists as drawn.
 */
export function getPlannerEditWrites(
  { org, songLists, currentWeekDate, fillStartDate }: PlannerEditContext,
  edit: PlannerEdit,
): PlannerEditWrites {
  const unwrittenPassedSundays =
    org?.type === OrgType.Church ? getUnwrittenPassedSundays(fillStartDate, currentWeekDate) : [];
  const writtenDown = getPassedSundaysWrittenDown(unwrittenPassedSundays);
  if (!org || !writtenDown) {
    return { lists: edit({ shown: songLists, written: songLists }) };
  }
  let editLists: SongList[] = [];
  const changes = getChainedWrites(songLists, [
    (lists) => getPassedSundayRangeWrites(org, unwrittenPassedSundays, lists),
    (written) => {
      editLists = edit({ shown: songLists, written });
      // a church's weekly lists are its Sunday worship services; a list left with no entry is one to remove
      const sundays = editLists.filter(isSundayWorshipService);
      return {
        update: sundays.filter(({ songs }) => songs.length > 0),
        remove: sundays.filter(({ songs }) => songs.length === 0),
      };
    },
  ]);
  if (editLists.length === 0) {
    return { lists: [] };
  }
  return {
    // a removed list comes back with no entry, which updateEach deletes
    lists: [...changes.update, ...changes.remove, ...editLists.filter((list) => !isSundayWorshipService(list))],
    writtenDown,
  };
}

/**
 * Writes `edit` (getPlannerEditWrites) through `updateEach`, as one batch (writePlannerWrites). Nothing when the edit
 * writes nothing.
 */
export async function applyPlannerEdit(
  context: PlannerEditContext & Pick<AllSongLists, 'updateEach'>,
  edit: PlannerEdit,
): Promise<void> {
  await writePlannerWrites(context.org, getPlannerEditWrites(context, edit), context.updateEach);
}

/**
 * Writes a planner's `lists` through `updateEach`, as one batch: an edit's (applyPlannerEdit) or the write-down
 * before a row change (writeDownSundaysBegun in planner_schema_column.tsx). When they write passed Sundays down,
 * inside writePassedSundaysDown, which records which, even when no list changes (Clear on a filled-in passed Sunday
 * whose fill it then takes off again, a write-down that finds every Sunday as it would write it): updateEach of no
 * list still starts the song-list sync that sends the mark, which the pending mark would otherwise hold the next
 * download back for. The pass it starts waits for writePassedSundaysDown to store the mark, and takes it inside the
 * requests of these lists. The sync is started here too once the write-down ends, whether or not it threw, so
 * whatever lists a throwing updateEach queued before it could start the sync still go up (the pass waits for the
 * write-down anyway, so a second start changes nothing otherwise). Otherwise updateEach only when there is a list to
 * write.
 *
 * synchronizeAllSongLists is started here rather than in writePassedSundaysDown, which use_current_org.tsx imports.
 */
export async function writePlannerWrites(
  org: Org | undefined,
  { lists, writtenDown }: PlannerEditWrites,
  updateEach: AllSongLists['updateEach'],
): Promise<void> {
  if (org && writtenDown) {
    try {
      await writePassedSundaysDown(org, writtenDown, () => updateEach(lists));
    } finally {
      void synchronizeAllSongLists();
    }
    return;
  }
  if (lists.length > 0) {
    await updateEach(lists);
  }
}

// a cell of the org's weekly row as the planner writes it: the Sunday and the row's repeat
export interface PlannerCell {
  org: Org | undefined;
  kind: RepeatKind;
  repeatFrom?: DateString;
  rowIndex: number;
  date: DateString;
  currentWeekDate: DateString;
  fillStartDate: DateString;
}

// setting the cell to `entry`, or clearing it with `entry` undefined (getRepeatingCellWrites), in a repeating row or a
// church's row that does not repeat
export function getCellEdit(cell: PlannerCell & { entry: SongListEntry | undefined }): PlannerEdit {
  return ({ shown, written }) => getRepeatingCellWrites({ ...cell, songLists: shown, writtenSongLists: written });
}

// a paste of `cells` (getPasteWrites), which writes entries only, so it is built on the lists as written down
export function getPasteEdit(paste: Omit<Parameters<typeof getPasteWrites>[0], 'songLists'>): PlannerEdit {
  return ({ written }) => getPasteWrites({ ...paste, songLists: written });
}

// the column clear of `date` (getColumnClearWrite)
export function getColumnClearEdit(clear: {
  org: Org | undefined;
  schema: SongListSchema | undefined;
  date: DateString;
  currentWeekDate: DateString;
}): PlannerEdit {
  return ({ shown, written }) => {
    const list = getColumnClearWrite({ ...clear, songLists: shown, writtenSongLists: written });
    return list ? [list] : [];
  };
}

// the planner's lists and how it writes them, for a paste or a column clear
export type PlannerWriteContext = PlannerEditContext & Pick<AllSongLists, 'update' | 'updateEach'>;

/**
 * Writes the paste of `cells` (getPasteWrites) at the schema's `rows`: a church's through applyPlannerEdit, so a paste
 * while a passed Sunday is not yet written down stands; a school's or small group's exactly as before issue #4209, one
 * updateEach of the lists it builds on the lists as drawn, a paste of nothing included.
 */
export async function applyPaste(
  context: PlannerWriteContext,
  { rows, cells }: Pick<Parameters<typeof getPasteWrites>[0], 'rows' | 'cells'>,
): Promise<void> {
  const { org, songLists, currentWeekDate } = context;
  if (org?.type !== OrgType.Church) {
    await context.updateEach(getPasteWrites({ org, rows, cells, songLists, currentWeekDate }));
    return;
  }
  await applyPlannerEdit(context, getPasteEdit({ org, rows, cells, currentWeekDate }));
}

/**
 * Writes the column clear of `date` (getColumnClearWrite): a church's through applyPlannerEdit, so a clear while a
 * passed Sunday is not yet written down stands; a school's or small group's exactly as before issue #4209, its one
 * list through update, nothing when there is none.
 */
export async function applyColumnClear(
  context: PlannerWriteContext,
  { schema, date }: { schema: SongListSchema | undefined; date: DateString },
): Promise<void> {
  const { org, songLists, currentWeekDate } = context;
  if (org?.type !== OrgType.Church) {
    const list = getColumnClearWrite({ org, schema, date, songLists, currentWeekDate });
    if (list) {
      await context.update(list);
    }
    return;
  }
  await applyPlannerEdit(context, getColumnClearEdit({ org, schema, date, currentWeekDate }));
}
